import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';

declare const $: any;

const POPUP_OPEN_CLASS = 'code-block-popup-open';

/**
 * Full screen view of a guide code block, in the same dialog as the image preview.
 * A copy of the block is shown so the guide behind the dialog keeps its layout;
 * the copy button still works there since copy is handled by the document click listeners.
 *
 * Also holds the code block styles (header, dark theme, popup) used by `enhanceCodeBlock`.
 */
@Component({
  selector: 'cloudlabs-code-block-popup',
  templateUrl: './code-block-popup.component.html',
  styleUrls: ['./code-block-popup.component.scss'],
})
export class CodeBlockPopupComponent implements OnDestroy {
  /** Used for the Close button style, same as the image preview popup */
  @Input() appType: string;

  @ViewChild('codePopupHost') private host: ElementRef<HTMLElement>;

  showPopup = false;
  private resizeObserver: ResizeObserver;

  open(block: HTMLElement): void {
    if (!block || this.showPopup) {
      return;
    }
    this.showPopup = true;
    // Lets the copy/paste toasts show above the dialog (see the stylesheet)
    document.body.classList.add(POPUP_OPEN_CLASS);
    // Wait for the dialog to render its content host
    setTimeout(() => {
      const host = this.host?.nativeElement;
      if (!host) {
        return;
      }
      const copy = block.cloneNode(true) as HTMLElement;
      copy.classList.remove('is-focused');
      // Bootstrap moves "title" to "data-original-title"; restore it for the copy's tooltips
      copy.querySelectorAll('[data-original-title]').forEach((el) => {
        el.setAttribute('title', el.getAttribute('data-original-title'));
        el.removeAttribute('data-original-title');
        el.removeAttribute('aria-describedby');
      });
      host.innerHTML = '';
      host.appendChild(copy);
      // Render tooltips inside the dialog: appended to body they sit below the dialog's z-index
      $(copy).find('[data-toggle="tooltip"]').tooltip({
        placement: 'bottom',
        trigger: 'hover',
        container: host.closest('.p-dialog') || 'body',
      });
      // Lines wrap in the popup, so size the line numbers to the wrapped lines right away
      // (before the first paint, so there is no visible jump) and again on any size change.
      const pre = copy.querySelector('pre');
      this.sizeWrappedLineNumbers(pre);
      if (pre && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.sizeWrappedLineNumbers(pre));
        this.resizeObserver.observe(pre);
      }
    });
  }

  close(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.host) {
      $(this.host.nativeElement).find('[data-toggle="tooltip"]').tooltip('dispose');
    }
    this.showPopup = false;
    document.body.classList.remove(POPUP_OPEN_CLASS);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.body.classList.remove(POPUP_OPEN_CLASS);
  }

  /**
   * Same idea as Prism's lineNumbers.resize: give each line number the height of its
   * (possibly wrapped) code line. Prism measures on-screen sizes, which are shrunk while the
   * dialog's open animation scales it, so here the measured heights are divided by that scale.
   */
  private sizeWrappedLineNumbers(pre: HTMLElement): void {
    const code = pre?.querySelector('code');
    const rows = pre?.querySelector('.line-numbers-rows');
    if (!code || !rows || !pre.offsetWidth) {
      return;
    }
    const scale = pre.getBoundingClientRect().width / pre.offsetWidth || 1;
    const lines = code.textContent.split(/\n(?!$)/g);
    const sizer = document.createElement('span');
    sizer.style.display = 'block';
    code.appendChild(sizer);
    sizer.textContent = '0';
    const oneLineHeight = sizer.getBoundingClientRect().height / scale;
    sizer.textContent = '';
    const lineEls = lines.map((line) => {
      if (!line || line.length <= 1) {
        return null;
      }
      const el = document.createElement('span');
      el.style.display = 'block';
      el.textContent = line;
      return sizer.appendChild(el);
    });
    lineEls.forEach((el, i) => {
      const row = rows.children[i] as HTMLElement;
      if (row) {
        row.style.height =
          (el ? el.getBoundingClientRect().height / scale : oneLineHeight) + 'px';
      }
    });
    sizer.remove();
  }
}
